import React, { useEffect, useState } from "react";
import { getPageBySlug } from "../lib/cms";
import { Map, Bus, Shield } from "lucide-react";

export default function KoretekniskAnlaeg() {
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadData() {
      try {
        const page = await getPageBySlug("koreteknisk-anlaeg");
        setPageData(page);
      } catch (err) {
        console.error("Error loading page data:", err);
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, []);

  useEffect(() => {
    if (pageData) {
      document.title = pageData.title;
      const metaDesc = document.querySelector('meta[name="description"]');
      if (metaDesc) {
        metaDesc.setAttribute("content", pageData.meta_description || "");
      }
    }
  }, [pageData]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-brand-light">
        <div className="animate-pulse text-brand-primary font-bold text-lg">Henter indhold...</div>
      </div>
    );
  }

  return (
    <div className="bg-brand-light min-h-screen py-12 lg:py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-brand-primary/10 text-brand-primary text-xs font-bold uppercase tracking-wider mb-4">
            Køreteknisk anlæg
          </span>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black text-brand-dark tracking-tight mb-4">
            Køreteknisk anlæg
          </h1>
          <p className="text-stone-600 text-base sm:text-lg max-w-3xl mx-auto leading-relaxed">
            Læs om manøvrebane (kravlegård) og køreteknisk anlæg (glatbane), og se vores video om, hvordan det foregår.
          </p>
        </div>

        {/* Main Content Card */}
        <div className="bg-white rounded-3xl border border-stone-200 shadow-xl overflow-hidden">
          {/* Hero Image */}
          {pageData.hero_image && pageData.hero_image[0] && (
            <div className="h-64 sm:h-96 w-full relative">
              <img
                src={pageData.hero_image[0]}
                alt="Køreteknisk anlæg"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent flex items-end p-6 sm:p-10">
                <div>
                  <span className="px-3 py-1 bg-brand-primary text-white text-xs font-bold uppercase tracking-wider rounded-md mb-2 inline-block">Sikkerhed i fokus</span>
                  <p className="text-white text-xl sm:text-2xl font-bold">Manøvre- & glatbanekørsel</p>
                </div>
              </div>
            </div>
          )}

          {/* Verbatim Content Area */}
          <div className="p-6 sm:p-10 lg:p-12 space-y-12">
            <div
              className="prose max-w-none"
              dangerouslySetInnerHTML={{ __html: pageData.content }}
            />

            <div className="mt-8 aspect-video w-full max-w-3xl mr-auto rounded-2xl shadow-lg border overflow-hidden">
              <iframe
                width="560"
                height="315"
                src="https://www.youtube.com/embed/O4aHzvOYBH0?si=9BIo6PUbecIv2CVn"
                title="YouTube video player"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                referrerPolicy="strict-origin-when-cross-origin"
                allowFullScreen
                className="w-full h-full"
              ></iframe>
            </div>

            {/* Extra Info Cards */}
            <div className="border-t border-stone-100 pt-10 mt-10 grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="p-6 bg-stone-50 rounded-2xl border border-stone-200/60 space-y-3">
                <div className="h-10 w-10 rounded-xl bg-brand-primary/10 flex items-center justify-center text-brand-primary">
                  <Map className="h-5 w-5" />
                </div>
                <h4 className="font-bold text-brand-dark text-base">Hvor foregår det?</h4>
                <p className="text-stone-600 text-sm leading-relaxed">
                  Al kørsel på manøvrebane og glatbane foregår på Køreteknisk Anlæg i Viborg eller Støvrings Køreteknisk Anlæg.
                </p>
              </div>

              <div className="p-6 bg-stone-50 rounded-2xl border border-stone-200/60 space-y-3">
                <div className="h-10 w-10 rounded-xl bg-brand-primary/10 flex items-center justify-center text-brand-primary">
                  <Bus className="h-5 w-5" />
                </div>
                <h4 className="font-bold text-brand-dark text-base">Fælles transport</h4>
                <p className="text-stone-600 text-sm leading-relaxed">
                  Du skal ikke bekymre dig om transporten. We sørger for, at I bliver fragtet frem og tilbage med bus i fællesskab.
                </p>
              </div>

              <div className="p-6 bg-stone-50 rounded-2xl border border-stone-200/60 space-y-3">
                <div className="h-10 w-10 rounded-xl bg-brand-primary/10 flex items-center justify-center text-brand-primary">
                  <Shield className="h-5 w-5" />
                </div>
                <h4 className="font-bold text-brand-dark text-base">Sikker kørsel</h4>
                <p className="text-stone-600 text-sm leading-relaxed">
                  På banerne lærer du at mestre bilen under ekstreme forhold (f.eks. udskridning på glat vej), så du er tryg i trafikken.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
